import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import DashboardShell from "@/components/layout/DashboardShell";
import { api } from "@/lib/api";
import { setHaatId, getHaatId } from "@/lib/haat";
import { useT } from "@/lib/i18n";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Store, Plus, Users, Beef, Wallet, FileText, ArrowRight, CheckCircle2 } from "lucide-react";

const fmtINR = (n) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(Number(n) || 0);

export default function SubscriberHaats() {
  const { t } = useT();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/subscriber/haats").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const addHaat = async () => {
    if (name.trim().length < 2) { toast.error(t("haat.nameRequired")); return; }
    setBusy(true);
    try {
      await api.post("/subscriber/haats", { name });
      toast.success(t("haat.created"));
      setOpen(false); setName("");
      load();
      window.location.reload(); // refresh shell nav + switcher
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const openHaat = (h) => {
    setHaatId(h.haat_id);
    window.location.assign("/subscriber"); // full reload so all modules re-scope
  };

  const atLimit = data && data.haats.length >= data.haat_limit;

  return (
    <DashboardShell
      title={t("nav.myHaats")}
      subtitle={data ? `${data.haats.length} / ${data.haat_limit} · ${data.plan_name}` : ""}
      action={
        <Button className="am-btn-primary rounded-full" data-testid="add-haat-btn"
          onClick={() => atLimit
            ? toast.error(t("haat.limitReached").replace("{n}", String(data.haat_limit)))
            : setOpen(true)}>
          <Plus className="w-4 h-4 mr-1" /> {t("haat.add")}
        </Button>
      }
    >
      <div className="grid sm:grid-cols-2 gap-5 max-w-4xl">
        {(data?.haats || []).map((h) => {
          const selected = getHaatId() === h.haat_id;
          return (
            <div key={h.haat_id} className={`am-card p-6 ${selected ? "ring-2 ring-primary border-primary" : ""}`} data-testid={`haat-card-${h.haat_id}`}>
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-2xl bg-accent text-primary grid place-items-center"><Store className="w-6 h-6" /></div>
                <div className="flex-1 min-w-0">
                  <h2 className="font-display font-bold text-lg truncate">{h.name}</h2>
                  <span className="am-chip text-[10px]">{data.plan_name}</span>
                </div>
                {selected && <CheckCircle2 className="w-5 h-5 text-primary shrink-0" />}
              </div>
              <div className="grid grid-cols-2 gap-3 mt-5 text-sm">
                <div className="flex items-center gap-2"><Wallet className="w-4 h-4 text-primary" /><div><div className="font-bold">{fmtINR(h.today_collection)}</div><div className="text-[11px] text-muted-foreground">{t("haat.todayCollection")}</div></div></div>
                <div className="flex items-center gap-2"><FileText className="w-4 h-4 text-primary" /><div><div className="font-bold">{h.today_registrations}</div><div className="text-[11px] text-muted-foreground">{t("haat.todayRegistrations")}</div></div></div>
                <div className="flex items-center gap-2"><Beef className="w-4 h-4 text-primary" /><div><div className="font-bold">{h.today_animals}</div><div className="text-[11px] text-muted-foreground">{t("haat.todayAnimals")}</div></div></div>
                <div className="flex items-center gap-2"><Users className="w-4 h-4 text-primary" /><div><div className="font-bold">{h.active_helpers}</div><div className="text-[11px] text-muted-foreground">{t("haat.activeHelpers")}</div></div></div>
              </div>
              <Button variant={selected ? "outline" : "default"} className={`w-full mt-5 rounded-full ${selected ? "" : "am-btn-primary"}`}
                data-testid={`open-haat-${h.haat_id}`} onClick={() => openHaat(h)}>
                {t("haat.open")} <ArrowRight className="w-4 h-4 ml-1.5" />
              </Button>
            </div>
          );
        })}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="font-display">{t("haat.add")}</DialogTitle></DialogHeader>
          <Input data-testid="new-haat-name" className="am-input" placeholder={t("haat.namePlaceholder")} value={name} onChange={(e) => setName(e.target.value)} />
          <DialogFooter>
            <Button data-testid="save-haat-btn" className="am-btn-primary w-full" disabled={busy} onClick={addHaat}>{busy ? "…" : t("common.save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardShell>
  );
}
